import { useEffect, useState } from "react";
import europeMapImage from "@/assets/europe-map.webp";

/**
 * ScreenLoading — the canonical loading state. Shown by App + ChronicleLayout's
 * Suspense boundaries while a lazy chunk is in flight (Play.tsx, an engine,
 * a map component); reusable anywhere else that needs a loading indicator.
 *
 * Quiet for the first `delayMs` of being mounted (default 1000 ms). Only
 * shows the compass + caption if the chunk genuinely takes more than a
 * second to load — otherwise the player never sees a flash. While the
 * delay window is open the component renders an invisible
 * background-filling div so the page background stays uniform.
 *
 * Composition once visible (layered back → front):
 *   • Sepia Europe map background (the same `europe-map.webp` the
 *     StartScreen opens on), heavily blurred + darkened so the
 *     continent is felt rather than read — and so the loading screen
 *     looks like part of the same atlas the player is about to enter.
 *   • Cartography grid (a 5×5 % lattice at opacity ~0.04) — the
 *     StartScreen's grid carried into the loading state.
 *   • Four gold-dim corner brackets — same ornament as the
 *     StartScreen and the in-game chrome, framing the centre.
 *   • Soft radial gold glow at the centre — like candlelight under
 *     the compass — and four faint cardinal rays bleeding outward,
 *     plus an edge vignette and the project grain overlay on top.
 *   • Static compass ring with cardinal letters (C O E R) and tick
 *     marks — does NOT rotate, so the labels never become unreadable.
 *   • Spinning inner needle on top of the ring — the needle alone
 *     turns, the way a real compass needle does. 3.6 s cycle,
 *     centred precisely on the ring's pivot.
 *   • "LOADING" caption in the project's display face below.
 *
 * Keyframes are inline in a <style> tag so this fallback works even when
 * an in-flight chunk hasn't loaded its CSS yet — which is exactly the
 * situation this component is shown for.
 */
export function ScreenLoading({ delayMs = 1000 }: { delayMs?: number } = {}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  if (!visible) {
    // Background-filling placeholder so the page doesn't flash if the
    // chunk loads quickly. Same dark base as the visible state, no
    // animation, no caption.
    return <div className="min-h-screen" style={{ background: "hsl(30 22% 6%)" }} />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ background: "hsl(30 22% 6%)" }}>
      {/* Sepia Europe map background — the same image the StartScreen
          opens on, heavily blurred + darkened so the player feels the
          atlas they are about to enter without reading any of it.
          Behind everything else. */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={europeMapImage}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "sepia(0.4) brightness(0.22) contrast(1.12) blur(6px)" }}
          draggable={false}
        />
        {/* Soften further so the compass reads cleanly on every viewport. */}
        <div className="absolute inset-0" style={{ background: "hsl(30 22% 6% / 0.55)" }} />
      </div>

      {/* Cartography grid — the StartScreen's 5×5 % lattice, carried
          into the loading state so the two screens share the same
          paper. Just above the map, just under everything else. */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={`h-${i}`}
            className="absolute left-0 right-0 border-t border-foreground"
            style={{ top: `${(i + 1) * 5}%` }}
          />
        ))}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={`v-${i}`}
            className="absolute top-0 bottom-0 border-l border-foreground"
            style={{ left: `${(i + 1) * 5}%` }}
          />
        ))}
      </div>

      {/* Four gold-dim corner brackets — same ornament as the
          StartScreen and the in-game chrome, framing the compass. */}
      <div className="hidden sm:block absolute top-6 left-6 w-16 h-16 border-t border-l border-gold-dim/30 pointer-events-none" aria-hidden="true" />
      <div className="hidden sm:block absolute top-6 right-6 w-16 h-16 border-t border-r border-gold-dim/30 pointer-events-none" aria-hidden="true" />
      <div className="hidden sm:block absolute bottom-6 left-6 w-16 h-16 border-b border-l border-gold-dim/30 pointer-events-none" aria-hidden="true" />
      <div className="hidden sm:block absolute bottom-6 right-6 w-16 h-16 border-b border-r border-gold-dim/30 pointer-events-none" aria-hidden="true" />

      {/* Soft radial gold glow at the centre — like candlelight under
          the compass. Above the map, behind the chrome. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 50% 50%, hsl(43, 70%, 35% / 0.22) 0%, hsl(43, 60%, 20% / 0.08) 28%, transparent 60%)",
        }}
      />
      {/* Four faint cardinal rays bleeding from the centre — gives the
          chrome a sense that the compass is shedding light across the
          map. Static; the needle on top is the only moving element. */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          background:
            "radial-gradient(ellipse 60% 1px at 50% 50%, hsl(43, 72%, 55%) 0%, transparent 75%)," +
            "radial-gradient(ellipse 1px 60% at 50% 50%, hsl(43, 72%, 55%) 0%, transparent 75%)",
        }}
      />
      {/* Edge vignette — dim the corners so the eye lands on the centre. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 50% 50%, transparent 35%, hsl(30 22% 3% / 0.55) 100%)",
        }}
      />
      {/* Project grain overlay — same one used across the rest of the
          chrome. Keeps the loading screen feeling part of the game. */}
      <div className="absolute inset-0 pointer-events-none grain-overlay" />

      {/* Content — fades in once the delay window has passed, so the
          first sight isn't a jolt. */}
      <div
        className="relative flex flex-col items-center gap-6"
        role="status"
        aria-label="Loading"
        style={{ animation: "coe-loading-fade 350ms ease-out both" }}
      >
        <div className="relative" style={{ width: 96, height: 96 }}>
          {/* Soft glow halo immediately under the compass. */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: "radial-gradient(circle, hsl(43, 80%, 65% / 0.22) 0%, transparent 65%)",
              filter: "blur(8px)",
            }}
          />

          {/* STATIC layer: outer ring, cardinal letters, tick marks. */}
          <svg viewBox="0 0 120 120" className="absolute inset-0 w-full h-full">
            <circle cx="60" cy="60" r="55" fill="none" stroke="hsl(var(--gold))" strokeWidth="1.2" opacity="0.55" />
            <circle cx="60" cy="60" r="50" fill="none" stroke="hsl(var(--gold))" strokeWidth="0.5" opacity="0.28" />
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 30 * Math.PI) / 180;
              const r1 = 47, r2 = 50;
              return (
                <line
                  key={i}
                  x1={60 + r1 * Math.sin(angle)}
                  y1={60 - r1 * Math.cos(angle)}
                  x2={60 + r2 * Math.sin(angle)}
                  y2={60 - r2 * Math.cos(angle)}
                  stroke="hsl(var(--gold))"
                  strokeWidth="0.7"
                  opacity="0.45"
                />
              );
            })}
            <line x1="25" y1="25" x2="45" y2="45" stroke="hsl(var(--gold))" strokeWidth="0.7" opacity="0.32" />
            <line x1="95" y1="25" x2="75" y2="45" stroke="hsl(var(--gold))" strokeWidth="0.7" opacity="0.32" />
            <line x1="25" y1="95" x2="45" y2="75" stroke="hsl(var(--gold))" strokeWidth="0.7" opacity="0.32" />
            <line x1="95" y1="95" x2="75" y2="75" stroke="hsl(var(--gold))" strokeWidth="0.7" opacity="0.32" />
            <text x="60" y="23" textAnchor="middle" fill="hsl(var(--gold))" fontSize="7" fontFamily="Cinzel, serif" fontWeight="700" opacity="0.85">C</text>
            <text x="97" y="63" textAnchor="middle" fill="hsl(var(--gold))" fontSize="7" fontFamily="Cinzel, serif" fontWeight="700" opacity="0.85">O</text>
            <text x="60" y="103" textAnchor="middle" fill="hsl(var(--gold))" fontSize="7" fontFamily="Cinzel, serif" fontWeight="700" opacity="0.85">E</text>
            <text x="23" y="63" textAnchor="middle" fill="hsl(var(--gold))" fontSize="7" fontFamily="Cinzel, serif" fontWeight="700" opacity="0.85">R</text>
          </svg>

          {/* SPINNING layer: only the four cardinal arrows + the pivot.
              Centred on the SVG midpoint (60, 60) so rotation is purely
              in-place, no wobble. */}
          <svg
            viewBox="0 0 120 120"
            className="absolute inset-0 w-full h-full"
            style={{
              animation: "coe-needle-spin 3.6s linear infinite",
              transformOrigin: "50% 50%",
              willChange: "transform",
            }}
          >
            <polygon points="60,10 64,50 60,45 56,50" fill="hsl(var(--gold))" opacity="0.95" />
            <polygon points="60,110 64,70 60,75 56,70" fill="hsl(var(--gold-dim))" opacity="0.7" />
            <polygon points="10,60 50,56 45,60 50,64" fill="hsl(var(--gold-dim))" opacity="0.7" />
            <polygon points="110,60 70,56 75,60 70,64" fill="hsl(var(--gold-dim))" opacity="0.7" />
            <circle cx="60" cy="60" r="5" fill="hsl(var(--gold))" opacity="0.85" />
            <circle cx="60" cy="60" r="2" fill="hsl(30 22% 6%)" />
          </svg>
        </div>

        <span
          className="font-display text-[11px] uppercase tracking-[0.32em]"
          style={{ color: "hsl(43, 30%, 58% / 0.85)", textShadow: "0 0 8px hsl(43, 60%, 30% / 0.4)" }}
        >
          Loading
        </span>
      </div>

      <style>{`
        @keyframes coe-needle-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes coe-loading-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ScreenLoading;
